import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTheme } from "../../context/ThemeContext";
import { getInputStyles, getMutedText } from "../../utils/themeUtils";
import { getMembership, getMyMembershipsList } from "../../api/membershipAPI";
import { setMembership } from "../../store/slices/membershipSlice";
import { Users } from "lucide-react";
import { captureError } from "../../utils/sentry";
import { toast } from "react-toastify";

interface MembershipListRow {
  membership_user_id: string;
  membership_id: string;
  role: "primary" | "secondary";
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  account_type: string | null;
  status: string | null;
}

/**
 * Renders nothing when the caller is only linked to a single
 * membership. When they have access to two or more (split-family
 * Mom / Dad case where one parent is primary on their own family AND
 * secondary on the other), renders a small dropdown bar that lets
 * them switch which membership the page is showing.
 *
 * Switching dispatches setMembership to Redux so every component
 * reading selectMembershipDetails picks up the new active family
 * without a full reload.
 */
const MembershipSwitcher: React.FC = () => {
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch();
  const token = localStorage.getItem("authToken") || "";
  const [memberships, setMemberships] = useState<MembershipListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string>("");
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const res = await getMyMembershipsList(token);
      if (cancelled) return;
      if (res?.success && Array.isArray(res.data)) {
        setMemberships(res.data);
        // Default to primary if present, else first.
        const primary = res.data.find((m: MembershipListRow) => m.role === "primary");
        const initial = primary?.membership_id || res.data[0]?.membership_id || "";
        setActiveId(initial);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) return null;
  if (memberships.length < 2) return null;

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = e.target.value;
    if (!newId || newId === activeId) return;
    setSwitching(true);
    setActiveId(newId);
    const previousId = activeId;
    try {
      const response = await getMembership(token, newId);
      if (response?.data?.data) {
        dispatch(setMembership(response.data.data));
      }
    } catch (err) {
      // try/finally with NO catch before this. setActiveId(newId) has already
      // run, so a failed switch left the dropdown showing a family that Redux
      // had never loaded — and the rejection was unhandled. Roll the selection
      // back so the UI matches the store.
      captureError(err, { component: "MembershipSwitcher", operation: "switchMembership" }, "warning");
      setActiveId(previousId);
      toast.error("Couldn't switch account. Please try again.");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 mb-3 rounded border text-sm ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"
      }`}
    >
      <Users size={14} className={getMutedText(isDarkMode)} />
      <span className={getMutedText(isDarkMode)}>Viewing:</span>
      <select
        value={activeId}
        onChange={handleChange}
        disabled={switching}
        className={`flex-1 px-2 py-1 rounded text-sm ${getInputStyles(isDarkMode)} disabled:opacity-50`}
      >
        {memberships.map((m) => (
          <option key={m.membership_id} value={m.membership_id}>
            {[m.first_name, m.last_name].filter(Boolean).join(" ") || m.email || "(unnamed)"}
            {m.role === "primary" ? " — yours" : " — co-managed"}
          </option>
        ))}
      </select>
      {switching && <span className={`text-xs ${getMutedText(isDarkMode)}`}>Switching…</span>}
    </div>
  );
};

export default MembershipSwitcher;
